import { Link, useNavigate } from "@tanstack/react-router";
import {
	Check,
	ChevronRight,
	Copy,
	ExternalLink,
	HardDrive,
	Laptop,
	LogIn,
	Plus,
	RefreshCw,
	Search,
	Trash2,
	Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ConnectorCatalogListSkeleton } from "@/components/agents/catalog-picker";
import { AppInstallButton } from "@/components/connectors/app-install";
import {
	ConnectorDisplay,
	formatRuntimePlatforms,
	getAuthMethodLabels,
} from "@/components/connectors/connector-display";
import { FacetChips } from "@/components/connectors/facet-chips";
import { ConnectionSetupChoices } from "@/components/connectors/setup-options";
import {
	CAPABILITY_LABELS,
	deviceName,
} from "@/components/devices/device-display";
import { DeviceSelectItem } from "@/components/devices/device-select-item";
import { MemberPicker } from "@/components/member-picker";
import { CreateSandboxForm } from "@/components/sandboxes/create-sandbox-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormDraftConflict } from "@/components/ui/form-draft-conflict";
import { FormLoadingShell } from "@/components/ui/form-loading-shell";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { useConnectorSchemaPeek } from "@/hooks/use-connector-schema-peek";
import type { ConnectionItem, ConnectorDefinitionItem } from "@/lib/api";
import {
	useActiveAuthRun,
	useAuthRun,
	useConnectorOperations,
	useDevices,
	useReauthenticateConnection,
} from "@/lib/api/connections";
import {
	type InferenceCatalogItem,
	useInferenceProviderCatalog,
} from "@/lib/api/inference-providers";
import {
	type SandboxProviderCatalogItem,
	useCreateSandbox,
	useSandboxes,
} from "@/lib/api/sandboxes";
import {
	buildConnectorApps,
	type ConnectorAppEntry,
} from "@/lib/connectors/app-catalog";
import { deviceInWorkspace } from "@/lib/connectors/device-rows";
import {
	CONNECTOR_FILTER_LABELS,
	type ConnectorFilter,
	connectorMatchesFilter,
	deriveConnectorFacets,
} from "@/lib/connectors/facets";
import { INFERENCE_ROW_KEY_PREFIX } from "@/lib/connectors/inference-rows";
import {
	buildSandboxProviderCatalogEntries,
	parseSandboxProviderKey,
	SANDBOX_PROVIDER_KEY_PREFIX,
	sandboxCatalogFacets,
	sandboxProviderFaviconDomain,
	sandboxProviderLabel,
} from "@/lib/connectors/sandbox-rows";
import type {
	ActionSchemaEntry,
	FeedSchemaEntry,
} from "@/lib/connectors/schema-sections";
import { formatTimeAgo } from "@/lib/format-utils";
import { gatewayOrigin } from "@/lib/gateway-origin";
import { lobuDeepLink } from "@/lib/lobu-deep-link";
import { cn, FILLED_BOX } from "@/lib/utils";
import { ArtifactRenderer } from "./auth-artifact-renderer";
import { AuthFlowDialog } from "./auth-flow-dialog";
import {
	connectorAuthBaseUrl,
	getMethodLabel,
	getRequiredOAuthScopes,
	type OAuthMethod,
} from "./auth-helpers";
import { ConnectionActionsSection } from "./connection-settings-tab";
import { ActionsSchemaPanel, FeedsPanel } from "./connector-panels";
import { DynamicConnectorForm } from "./dynamic-connector-form";
import { OAuthScopeSelector } from "./oauth-permissions";
import { useConnectionForm } from "./use-connection-form";

// ============================================================
// Component
// ============================================================

// Catalog filter chips use the shared capability filters. Inventory-only
// filters such as `agents` do not apply to connector definitions.
const CATALOG_FILTERS: ReadonlyArray<ConnectorFilter> = [
	"all",
	"data",
	"chat",
	"actions",
	"inference",
	"runtime",
	"device",
];

/** A model-provider catalog entry's facets: inference and nothing else. */
const INFERENCE_ONLY_FACETS = {
	data: false,
	chat: false,
	actions: false,
	audience: false,
	inference: true,
	runtime: false,
} as const;

const CREATE_OAUTH_APP_PROFILE_VALUE = "__create_oauth_app_profile__";
const CREATE_OAUTH_ACCOUNT_PROFILE_VALUE = "__create_oauth_account_profile__";
const CREATE_BROWSER_PROFILE_VALUE = "__create_browser_profile__";
const SERVERLESS_DEVICE_VALUE = "__serverless__";
const ADD_DEVICE_VALUE = "__add_device__";

const CONNECTOR_AUTH_BASE_URL = connectorAuthBaseUrl();
const OAUTH_REDIRECT_URI = `${CONNECTOR_AUTH_BASE_URL}/connect/oauth/callback`;

function OAuthSetupInfo({ method }: { method: OAuthMethod }) {
	const [copied, setCopied] = useState(false);
	const rawInstructions = method.setupInstructions || method.description;

	const instructions = rawInstructions
		? rawInstructions.replace(/\{\{redirect_uri\}\}/g, OAUTH_REDIRECT_URI)
		: null;

	return (
		<div className="space-y-2 rounded-md border bg-muted/30 p-3">
			{instructions && (
				<p className="text-xs text-muted-foreground">{instructions}</p>
			)}
			<div className="space-y-1">
				<p className="text-xs font-medium text-muted-foreground">
					Redirect URI
				</p>
				<div className="flex items-center gap-1.5">
					<code className="flex-1 rounded bg-muted px-2 py-1 text-xs font-mono break-all">
						{OAUTH_REDIRECT_URI}
					</code>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-7 w-7 shrink-0 p-0"
						onClick={() => {
							void navigator.clipboard.writeText(OAUTH_REDIRECT_URI);
							setCopied(true);
							setTimeout(() => setCopied(false), 2000);
						}}
					>
						{copied ? (
							<Check className="h-3.5 w-3.5" />
						) : (
							<Copy className="h-3.5 w-3.5" />
						)}
					</Button>
				</div>
			</div>
		</div>
	);
}

function ConnectorPreview({
	connector,
	ownerSlug,
	existingConnectionCount = 0,
	onInstall,
	onBack,
	isInstalling,
	installError,
}: {
	connector: ConnectorDefinitionItem;
	ownerSlug?: string;
	existingConnectionCount?: number;
	onInstall: () => void;
	onBack: () => void;
	isInstalling: boolean;
	installError: string | null;
}) {
	const authMethodLabels = getAuthMethodLabels(connector.auth_schema);
	const requiredPlatforms = formatRuntimePlatforms(
		connector.runtime?.platforms,
	);
	const runtimeScopes = connector.runtime?.scopes ?? [];
	const { data: devices = [] } = useDevices();
	const capability = connector.required_capability ?? null;
	const connectedDevice = capability
		? devices.find(
				(d) =>
					(!ownerSlug || deviceInWorkspace(d, ownerSlug)) &&
					d.online &&
					d.capabilities.includes(capability),
			)
		: undefined;
	const {
		inspectFeed: inspectPreviewFeed,
		inspectAction: inspectPreviewAction,
	} = useConnectorSchemaPeek();
	const previewFeedsSchema = (connector.feeds_schema ?? {}) as Record<
		string,
		FeedSchemaEntry
	>;
	const previewActionsSchema = (connector.actions_schema ?? {}) as Record<
		string,
		ActionSchemaEntry
	>;

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={onBack}
					className="h-7 px-2 text-xs"
				>
					Choose another connector
				</Button>
				<ConnectorDisplay
					connector={connector}
					showDescription={false}
					className="gap-2"
				/>
				{connector.version && (
					<Badge
						variant="secondary"
						className="h-4 px-1.5 py-0 text-[10px] font-mono"
					>
						v{connector.version}
					</Badge>
				)}
				{connector.catalog_origin === "org" && (
					<Badge variant="secondary" className="h-4 px-1.5 py-0 text-[10px]">
						Org-installed
					</Badge>
				)}
			</div>

			{(requiredPlatforms ||
				connector.required_capability ||
				authMethodLabels.length > 0 ||
				existingConnectionCount > 0) && (
				<div className="flex flex-wrap items-center gap-1.5">
					{existingConnectionCount > 0 && (
						<Badge variant="secondary" className="h-4 px-1.5 py-0 text-[10px]">
							{existingConnectionCount} existing connection
							{existingConnectionCount === 1 ? "" : "s"}
						</Badge>
					)}
					{requiredPlatforms && (
						<Badge
							variant="secondary"
							className="h-4 gap-1 px-1.5 py-0 text-[10px]"
						>
							<HardDrive className="h-2.5 w-2.5" />
							Requires {requiredPlatforms}
						</Badge>
					)}
					{connector.required_capability && (
						<Badge variant="secondary" className="h-4 px-1.5 py-0 text-[10px]">
							Needs {connector.required_capability}
						</Badge>
					)}
					{authMethodLabels.map((label) => (
						<Badge
							key={label}
							variant="secondary"
							className="h-4 px-1.5 py-0 text-[10px]"
						>
							{label}
						</Badge>
					))}
				</div>
			)}

			{connector.description && (
				<div className={cn(FILLED_BOX, "p-3")}>
					<p className="text-sm text-muted-foreground">
						{connector.description}
					</p>
				</div>
			)}

			{(connector.source_uri || runtimeScopes.length > 0) && (
				<div className="space-y-1 rounded-lg border bg-muted/30 p-3 text-xs">
					{connector.source_uri && (
						<div className="flex items-center gap-2">
							<span className="font-medium text-muted-foreground uppercase tracking-wider text-[10px]">
								Source
							</span>
							<a
								href={connector.source_uri}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 break-all font-mono text-primary hover:underline"
							>
								{connector.source_uri}
								<ExternalLink className="h-3 w-3 shrink-0" />
							</a>
						</div>
					)}
					{runtimeScopes.length > 0 && (
						<div className="flex flex-wrap items-center gap-1.5">
							<span className="font-medium text-muted-foreground uppercase tracking-wider text-[10px]">
								Scopes
							</span>
							{runtimeScopes.map((scope) => (
								<Badge
									key={scope}
									variant="secondary"
									className="h-4 px-1.5 py-0 text-[10px] font-mono"
								>
									{scope}
								</Badge>
							))}
						</div>
					)}
				</div>
			)}

			<FeedsPanel
				feedsSchema={previewFeedsSchema}
				onInspect={inspectPreviewFeed}
			/>
			<ActionsSchemaPanel
				actionsSchema={previewActionsSchema}
				ownerSlug={ownerSlug}
				connectorKey={connector.key}
				onInspect={inspectPreviewAction}
			/>

			{installError && (
				<div className="rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-600 dark:text-red-400">
					{installError}
				</div>
			)}

			{connector.required_capability ? (
				connectedDevice ? (
					<div className="space-y-2">
						<div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
							Available on {deviceName(connectedDevice)}
							{connectedDevice.last_seen_at
								? ` · last seen ${formatTimeAgo(connectedDevice.last_seen_at)}`
								: ""}
						</div>
						{ownerSlug ? (
							<Button asChild variant="secondary" className="w-full">
								<Link
									to="/$owner/connectors/device/$deviceId"
									params={{ owner: ownerSlug, deviceId: connectedDevice.id }}
								>
									Open device
								</Link>
							</Button>
						) : null}
					</div>
				) : (
					// Device connectors aren't installed from a form — the data lives
					// on a Mac. Firing `lobu://grant/<cap>` asks the signed app to
					// show the native permission prompt; once granted, the device
					// advertises the capability and device-reconcile wires the
					// connection, which then shows up in the list automatically.
					<Button
						onClick={() => {
							window.location.href = lobuDeepLink(
								`grant/${connector.required_capability}`,
							);
						}}
						className="w-full"
					>
						<Laptop className="mr-2 h-4 w-4" />
						Connect on this Mac
					</Button>
				)
			) : (
				<Button onClick={onInstall} disabled={isInstalling} className="w-full">
					{isInstalling ? (
						<>
							<Spinner size="lg" className="mr-2" />
							Installing...
						</>
					) : (
						"Install & Configure"
					)}
				</Button>
			)}
		</div>
	);
}

function CreateModeCapabilities({
	connector,
	ownerSlug,
}: {
	connector: ConnectorDefinitionItem;
	ownerSlug?: string;
}) {
	const { data: operations = [] } = useConnectorOperations(
		connector.installed ? connector.key : null,
	);
	const actionsSchema =
		operations.length > 0
			? Object.fromEntries(
					operations.map((operation) => [
						operation.operation_key,
						{
							...(connector.actions_schema?.[operation.operation_key] as
								| ActionSchemaEntry
								| undefined),
							name: operation.name,
							description: operation.description,
						},
					]),
				)
			: connector.actions_schema;
	const { inspectFeed, inspectAction } = useConnectorSchemaPeek();
	return (
		<>
			<FeedsPanel
				feedsSchema={connector.feeds_schema}
				onInspect={inspectFeed}
			/>
			<ActionsSchemaPanel
				actionsSchema={actionsSchema}
				onInspect={inspectAction}
				ownerSlug={ownerSlug}
				connectorKey={connector.key}
			/>
		</>
	);
}

function InteractivePairingPanel({
	connection,
	activeAuthRunId,
	onStart,
	onClear,
	onRecover,
	isStarting,
}: {
	connection: ConnectionItem;
	activeAuthRunId: number | null;
	onStart: () => void;
	onClear: () => void;
	onRecover: (runId: number) => void;
	isStarting: boolean;
}) {
	// On mount (or after reload), ask the API whether this connection already
	// has an in-flight auth run from the current user so we can resume rendering
	// its QR/artifact instead of showing "Pair device" again.
	const { data: activeLookup } = useActiveAuthRun(
		activeAuthRunId == null ? connection.id : null,
	);
	useEffect(() => {
		const recoveredId = activeLookup?.run_id;
		if (activeAuthRunId == null && recoveredId) {
			onRecover(recoveredId);
		}
	}, [activeAuthRunId, activeLookup?.run_id, onRecover]);

	const { data: run, isLoading: isLoadingRun } = useAuthRun(activeAuthRunId);
	const isTerminal =
		run?.status === "completed" ||
		run?.status === "failed" ||
		run?.status === "cancelled";

	const needsReauth =
		connection.status === "pending_auth" ||
		connection.status === "error" ||
		(connection.auth_profile_status != null &&
			connection.auth_profile_status !== "active");

	const hasActiveRun = activeAuthRunId != null && !isTerminal;

	return (
		<div className="space-y-3 rounded-md border bg-background p-3">
			<p className="text-xs text-muted-foreground">
				This connector pairs interactively (e.g. QR code or device link).
				Artifacts are only shown to the person who initiates the pairing.
			</p>

			{!hasActiveRun && (
				<Button
					type="button"
					variant={needsReauth ? "default" : "outline"}
					size="sm"
					className="h-8"
					disabled={isStarting}
					onClick={onStart}
				>
					{isStarting ? (
						<>
							<Spinner size="md" className="mr-1.5" />
							Starting…
						</>
					) : needsReauth ? (
						"Pair device"
					) : (
						"Reset pairing"
					)}
				</Button>
			)}

			{hasActiveRun && (
				<div className="space-y-3">
					{isLoadingRun && !run && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Spinner size="lg" /> Preparing…
						</div>
					)}

					{run && !run.artifact && !isTerminal && (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Spinner size="lg" /> Waiting for connector…
						</div>
					)}

					{run?.artifact && !isTerminal && activeAuthRunId != null && (
						<ArtifactRenderer artifact={run.artifact} runId={activeAuthRunId} />
					)}

					<div className="flex items-center justify-between gap-2">
						<span className="text-xs text-muted-foreground">
							{run?.status === "pending"
								? "Starting…"
								: run?.status === "running"
									? "Waiting for you…"
									: ""}
						</span>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="h-8"
							onClick={onClear}
						>
							Cancel
						</Button>
					</div>
				</div>
			)}

			{isTerminal && run?.status === "completed" && (
				<div className="text-sm text-emerald-600">Paired successfully.</div>
			)}
			{isTerminal && run?.status === "failed" && (
				<div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive">
					{run.error_message || "Authentication failed."}
				</div>
			)}
		</div>
	);
}

interface ConnectionFormProps {
	onClose: () => void;
	onCreated?: (connectionId: number, connectorKey: string) => void;
	organizationId: string;
	editingConnection?: ConnectionItem | null;
	connections?: ConnectionItem[];
	/** Auto-select this connector for the new-connection flow. */
	initialConnectorKey?: string;
	ownerSlug?: string;
	/** Where "Back" navigates when this form was opened via a deep link with a
	 *  pre-selected connector (page route, not the picker dialog). */
	onDeepLinkBack?: () => void;
}

export function ConnectionForm({
	onClose,
	onCreated,
	organizationId,
	editingConnection,
	connections = [],
	initialConnectorKey,
	ownerSlug,
	onDeepLinkBack,
}: ConnectionFormProps) {
	const form = useConnectionForm({
		open: true,
		onCreated,
		onOpenChange: (next) => {
			if (!next) onClose();
		},
		organizationId,
		editingConnection,
		connections,
		initialConnectorKey,
		onDeepLinkBack,
	});
	const reauthenticate = useReauthenticateConnection();
	const navigate = useNavigate();
	// Model providers surface in the picker alongside connectors (they carry the
	// inference facet); each entry links to its connector detail page, where the
	// create-provider form lives. Sandbox provider kinds do the same with the
	// runtime facet — pick Vercel, fill its credentials, no second provider step.
	const { data: inferenceCatalog = [] } = useInferenceProviderCatalog();
	const { data: sandboxesPayload, isLoading: isSandboxesLoading } =
		useSandboxes({
			enabled: Boolean(ownerSlug),
		});
	// Filter for the catalog picker (mirrors the connectors grid's chips). Uses
	// the same shared filter vocabulary. View-only state, so it lives here rather
	// than in the form hook.
	const [catalogFilter, setCatalogFilter] = useState<ConnectorFilter>("all");
	// Narrows the catalog to what the org already has (installed or connected).
	// A picked sandbox provider kind. Sandboxes are not `connections`, so they
	// cannot ride `form.selectedConnector` — but they ARE picked from this same
	// catalog, so they select IN PLACE here rather than navigating to the
	// connector detail route. Same page chrome, same Back affordance.
	const [selectedSandboxKind, setSelectedSandboxKind] = useState<string | null>(
		// Deep link (/connectors/create/sandbox-provider:vercel, and the forwarded
		// old detail-route link) opens straight on the credential form.
		() => parseSandboxProviderKey(initialConnectorKey ?? ""),
	);
	const isAppInstallCreateFlow =
		!form.isEditMode && form.activeMethod?.type === "app_installation";

	const deviceLine = editingConnection?.device_worker_id ? (
		<p className="text-xs text-muted-foreground">
			Connected through{" "}
			{editingConnection.device_worker_id && ownerSlug ? (
				<Link
					to="/$owner/connectors/device/$deviceId"
					params={{
						owner: ownerSlug,
						deviceId: editingConnection.device_worker_id,
					}}
					className="text-foreground hover:underline"
				>
					{editingConnection.device_label ||
						editingConnection.device_platform ||
						"this device"}
				</Link>
			) : (
				<span className="text-foreground">
					{editingConnection.device_label ||
						editingConnection.device_platform ||
						"this device"}
				</span>
			)}
			{editingConnection.device_online === false
				? " · offline"
				: editingConnection.device_last_seen_at
					? ` · last seen ${formatTimeAgo(editingConnection.device_last_seen_at)}`
					: ""}
		</p>
	) : null;

	// The parent page (`/$owner/connectors`) supplies its own framing — the
	// per-step Back row already names the active connector, so skip the
	// redundant h1 here.
	const header = deviceLine;

	const body = (
		<div className="space-y-6 pb-6">
			{/* Connector Preview (uninstalled) */}
			{!form.selectedConnector && form.previewConnector && (
				<ConnectorPreview
					ownerSlug={ownerSlug}
					connector={form.previewConnector}
					existingConnectionCount={
						connections.filter(
							(c) => c.connector_key === form.previewConnector?.key,
						).length
					}
					onInstall={() => {
						// Land on the same URL picking an already-installed connector
						// lands on, so install and pick end in one place.
						void form.handleInstallPreviewedConnector().then((installed) => {
							if (!installed || !ownerSlug) return;
							void navigate({
								to: "/$owner/connectors/create/$connectorKey",
								params: { owner: ownerSlug, connectorKey: installed.key },
							});
						});
					}}
					onBack={
						initialConnectorKey && onDeepLinkBack
							? onDeepLinkBack
							: form.handleBack
					}
					isInstalling={form.installConnectorMutation.isPending}
					installError={form.installError}
				/>
			)}

			{/* A picked sandbox provider kind renders its credential form right here,
          with the same Back + connector-chip header the connector branch uses
          below — one "Add a connection" page, not a second surface. */}
			{selectedSandboxKind && (
				<SandboxKindCreatePanel
					providerKind={selectedSandboxKind}
					credentialFields={
						sandboxesPayload?.providerCatalog.find(
							(provider) => provider.id === selectedSandboxKind,
						)?.credentialFields
					}
					isProviderCatalogLoading={isSandboxesLoading}
					ownerSlug={ownerSlug ?? ""}
					onBack={() => {
						setSelectedSandboxKind(null);
						if (initialConnectorKey && ownerSlug)
							void navigate({
								to: "/$owner/connectors/create",
								params: { owner: ownerSlug },
							});
					}}
				/>
			)}

			{/* Connector Selection Grid */}
			{!selectedSandboxKind &&
				!form.selectedConnector &&
				!form.previewConnector && (
					<div className="space-y-3">
						{/* Install Custom Connector */}
						<div className={cn(FILLED_BOX, "w-full")}>
							{!form.showInstallForm ? (
								<button
									type="button"
									className="flex items-center gap-3 p-3 w-full text-left text-muted-foreground hover:text-foreground transition-colors"
									onClick={() => form.setShowInstallForm(true)}
								>
									<div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
										<Upload className="h-4 w-4" />
									</div>
									<div>
										<p className="text-sm font-medium">
											Install Custom Connector
										</p>
										<p className="text-xs text-muted-foreground">
											Install from a source file URL or MCP server URL
										</p>
									</div>
								</button>
							) : (
								<div className="p-3 space-y-3">
									<p className="text-sm font-medium">Install from URL</p>
									<Input
										placeholder="https://… (TypeScript source or MCP server URL)"
										value={form.installSourceUrl}
										onChange={(e) => {
											form.setInstallSourceUrl(e.target.value);
											form.setInstallError(null);
										}}
										className="text-xs font-mono"
									/>
									<p className="text-xs text-muted-foreground">
										URLs ending in .ts/.js are installed as source connectors.
										Other URLs are probed as MCP servers.
									</p>
									{form.installError && (
										<p className="text-xs text-destructive">
											{form.installError}
										</p>
									)}
									<div className="flex justify-end gap-2">
										<Button
											variant="outline"
											size="sm"
											className="h-7 text-xs"
											onClick={() => {
												form.setShowInstallForm(false);
												form.setInstallSourceUrl("");
												form.setInstallError(null);
											}}
										>
											Cancel
										</Button>
										<Button
											size="sm"
											className="h-7 text-xs"
											disabled={
												form.installConnectorMutation.isPending ||
												!form.installSourceUrl.trim()
											}
											onClick={() => {
												form.setInstallError(null);
												const url = form.installSourceUrl.trim();
												const isSourceUrl = /\.(ts|js|mjs)(\?|$)/i.test(url);
												form.installConnectorMutation.mutate(
													isSourceUrl ? { source_url: url } : { mcp_url: url },
													{
														onSuccess: () => {
															form.setShowInstallForm(false);
															form.setInstallSourceUrl("");
														},
														onError: (error) => {
															form.setInstallError(
																error instanceof Error
																	? error.message
																	: "Failed to install connector",
															);
														},
													},
												);
											}}
										>
											{form.installConnectorMutation.isPending ? (
												<Spinner size="sm" className="mr-1" />
											) : null}
											Install
										</Button>
									</div>
								</div>
							)}
						</div>

						{/* Search */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder="Search connectors..."
								value={form.searchQuery}
								onChange={(e) => form.setSearchQuery(e.target.value)}
								className="pl-9"
							/>
						</div>

						{/* Shared connector-capability filters; inventory-only filters stay out. */}
						<div className="flex flex-wrap items-center gap-1.5">
							{CATALOG_FILTERS.map((key) => (
								<Button
									key={key}
									type="button"
									variant={catalogFilter === key ? "secondary" : "ghost"}
									size="sm"
									className="h-7 px-2 text-xs"
									onClick={() => setCatalogFilter(key)}
								>
									{CONNECTOR_FILTER_LABELS[key]}
								</Button>
							))}
						</div>
						{!form.showInstallForm && form.installError && (
							<p className="text-xs text-destructive">{form.installError}</p>
						)}

						{form.isLoadingDefs ? (
							<ConnectorCatalogListSkeleton />
						) : (
							(() => {
								const query = form.searchQuery.toLowerCase().trim();
								const connectionCounts = new Map<string, number>();
								for (const conn of connections) {
									connectionCounts.set(
										conn.connector_key,
										(connectionCounts.get(conn.connector_key) ?? 0) + 1,
									);
								}
								const filtered = form.connectorDefs
									.filter((c) => {
										// Device-bound connectors (non-null `runtime`, e.g. Apple
										// Health / Screen Time) need a paired Lobu device to run.
										// They're listed here with a "Requires <platform>" badge
										// so users discover them — pinning happens after selection.
										const matchesSearch =
											!query ||
											c.name.toLowerCase().includes(query) ||
											c.key.toLowerCase().includes(query) ||
											c.description?.toLowerCase().includes(query);
										if (!matchesSearch) return false;
										// Chip filter — the same shared matcher the connectors grid
										// uses, so a connector filters identically on both pages.
										return connectorMatchesFilter(
											{ connector: c, facets: deriveConnectorFacets(c) },
											catalogFilter,
										);
									})
									.sort((a, b) => a.name.localeCompare(b.name));
								// Split rather than sort-and-badge. The list already put installed
								// first, so every installed row carried an "Installed" badge to
								// explain an order you could not see — two devices saying one
								// thing. A heading says it once, and the two groups answer
								// different questions: what do I already have, and what else is
								// there.
								const isInstalled = (c: ConnectorDefinitionItem) =>
									c.installed || (connectionCounts.get(c.key) ?? 0) > 0;
								const installedRows = filtered.filter(isInstalled);
								const catalogRows = filtered.filter((c) => !isInstalled(c));
								// Model providers, listed after the connectors: same search box,
								// same chip matcher (their only facet is `inference`). They need
								// an owner slug to link to their detail page, so they're omitted
								// in ownerless embeds.
								const providerRows = ownerSlug
									? inferenceCatalog
											.filter((item) => {
												const matchesSearch =
													!query ||
													item.displayName.toLowerCase().includes(query) ||
													item.slug.toLowerCase().includes(query);
												if (!matchesSearch) return false;
												return connectorMatchesFilter(
													{
														connector: {
															key: `${INFERENCE_ROW_KEY_PREFIX}${item.slug}`,
															name: item.displayName,
															auth_schema: null,
														} as ConnectorDefinitionItem,
														facets: INFERENCE_ONLY_FACETS,
													},
													catalogFilter,
												);
											})
											.sort((a, b) =>
												a.displayName.localeCompare(b.displayName),
											)
									: [];
								const sandboxKindRows = ownerSlug
									? buildSandboxProviderCatalogEntries(
											sandboxesPayload?.availableProviders ?? [],
										)
											.filter((entry) => {
												const matchesSearch =
													!query ||
													entry.name.toLowerCase().includes(query) ||
													entry.key.toLowerCase().includes(query) ||
													entry.description?.toLowerCase().includes(query);
												if (!matchesSearch) return false;
												return connectorMatchesFilter(
													{
														connector: entry,
														facets: sandboxCatalogFacets(),
													},
													catalogFilter,
												);
											})
											.sort((a, b) => a.name.localeCompare(b.name))
									: [];
								// The first-party connector runtimes — Mac, extension, and
								// terminal/Docker workers. They belong here for the same
								// reason a sandbox kind does: from where the user stands, all
								// of it is "pick the thing that gets my agents access to X".
								// Search-only, no chip: an app is an install path, not a
								// capability, so no facet honestly describes one.
								const appRows =
									ownerSlug && catalogFilter === "all"
										? buildConnectorApps(ownerSlug, gatewayOrigin()).filter(
												(app) =>
													!query ||
													app.name.toLowerCase().includes(query) ||
													app.tagline.toLowerCase().includes(query),
											)
										: [];
								const synthRowCount =
									providerRows.length + sandboxKindRows.length + appRows.length;
								const renderConnectorIn =
									(rowCount: number) =>
									(connector: ConnectorDefinitionItem, idx: number) => {
										const isInstalling =
											form.installConnectorMutation.isPending &&
											form.installingConnectorKey === connector.key;
										const requiredPlatforms = formatRuntimePlatforms(
											connector.runtime?.platforms,
										);
										const authMethodLabels = getAuthMethodLabels(
											connector.auth_schema as Record<string, unknown> | null,
										);
										return (
											<button
												key={connector.key}
												type="button"
												className={`flex w-full items-center gap-3 bg-card px-3 py-2.5 text-left text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60 ${
													idx === rowCount - 1 ? "" : "border-b"
												}`}
												onClick={() => {
													// Installed connectors: open the dedicated create URL so
													// the browser URL matches the step (picker → configure)
													// and refresh/share land on the form, not the catalog.
													// Uninstalled installable ones stay in-page for the install
													// preview. Provider/sandbox rows use their own navigators.
													if (
														ownerSlug &&
														connector.installed !== false &&
														!parseSandboxProviderKey(connector.key) &&
														!connector.key.startsWith(INFERENCE_ROW_KEY_PREFIX)
													) {
														void navigate({
															to: "/$owner/connectors/create/$connectorKey",
															params: {
																owner: ownerSlug,
																connectorKey: connector.key,
															},
														});
														return;
													}
													void form.handleConnectorSelect(connector);
												}}
												disabled={
													form.installConnectorMutation.isPending &&
													form.installingConnectorKey !== connector.key
												}
											>
												<ConnectorDisplay
													connector={connector}
													className="min-w-0 flex-1"
												/>
												<div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
													{isInstalling ? (
														<Badge
															variant="secondary"
															className="h-4 shrink-0 gap-1 px-1.5 py-0 text-[10px]"
														>
															<Spinner size="xs" />
															Installing
														</Badge>
													) : null}
													{requiredPlatforms && (
														<Badge
															variant="secondary"
															className="h-4 shrink-0 gap-1 px-1.5 py-0 text-[10px]"
														>
															<HardDrive className="h-2.5 w-2.5" />
															Requires {requiredPlatforms}
														</Badge>
													)}
													{authMethodLabels.map((label) => (
														<Badge
															key={label}
															variant="secondary"
															className="h-4 shrink-0 px-1.5 py-0 text-[10px]"
														>
															{label}
														</Badge>
													))}
													{connector.login_enabled && (
														<Badge
															variant="secondary"
															className="h-4 shrink-0 gap-1 px-1.5 py-0 text-[10px]"
														>
															<LogIn className="h-2.5 w-2.5" />
															Login
														</Badge>
													)}
												</div>
												<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
											</button>
										);
									};

								// Same row pattern as renderConnector, but a provider entry
								// links to its connector detail page (which hosts the
								// create-provider form) instead of selecting in this form.
								const renderProvider = (
									item: InferenceCatalogItem,
									idx: number,
								) => (
									<Link
										key={`${INFERENCE_ROW_KEY_PREFIX}${item.slug}`}
										to="/$owner/connectors/$connectorKey"
										search={{ addAccount: true }}
										params={{
											owner: ownerSlug ?? "",
											connectorKey: `${INFERENCE_ROW_KEY_PREFIX}${item.slug}`,
										}}
										className={`flex w-full items-center gap-3 bg-card px-3 py-2.5 text-left text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground ${
											idx === providerRows.length - 1 &&
											sandboxKindRows.length + appRows.length === 0
												? ""
												: "border-b"
										}`}
									>
										<ConnectorDisplay
											connector={{
												key: `${INFERENCE_ROW_KEY_PREFIX}${item.slug}`,
												name: item.displayName,
												iconUrl: item.iconUrl,
											}}
											className="min-w-0 flex-1"
										/>
										<div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
											<FacetChips facets={INFERENCE_ONLY_FACETS} />
										</div>
										<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
									</Link>
								);

								// Sandbox provider kind — links to locked create form for that kind.
								const renderSandboxKind = (
									entry: ConnectorDefinitionItem,
									idx: number,
								) => (
									<button
										key={entry.key}
										type="button"
										onClick={() =>
											setSelectedSandboxKind(
												parseSandboxProviderKey(entry.key) ?? null,
											)
										}
										className={`flex w-full items-center gap-3 bg-card px-3 py-2.5 text-left text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground ${
											idx === sandboxKindRows.length - 1 && appRows.length === 0
												? ""
												: "border-b"
										}`}
									>
										<ConnectorDisplay
											connector={entry}
											className="min-w-0 flex-1"
										/>
										<div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
											<FacetChips facets={sandboxCatalogFacets()} />
											<Badge
												variant="secondary"
												className="h-4 shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground"
											>
												Available
											</Badge>
										</div>
										<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
									</button>
								);

								// A first-party app — its row links to the instruction page
								// under connectors, which is also where its paired devices
								// and connected clients are listed.
								const renderApp = (app: ConnectorAppEntry, idx: number) => (
									<Link
										key={app.id}
										to="/$owner/connectors/app/$appId"
										params={{ owner: ownerSlug ?? "", appId: app.id }}
										className={`flex w-full items-center gap-3 bg-card px-3 py-2.5 text-left text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground ${
											idx === appRows.length - 1 ? "" : "border-b"
										}`}
									>
										<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-dashed">
											{app.icon}
										</span>
										<div className="min-w-0 flex-1">
											<p className="truncate text-sm font-medium">{app.name}</p>
											<p className="truncate text-xs text-muted-foreground">
												{app.tagline}
											</p>
										</div>
										<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
									</Link>
								);

								if (
									filtered.length === 0 &&
									providerRows.length === 0 &&
									sandboxKindRows.length === 0 &&
									appRows.length === 0
								) {
									return (
										<div className="space-y-3 py-4 text-center">
											<div className="space-y-1">
												<p className="text-sm font-medium">
													{query
														? `No connectors match "${form.searchQuery}"`
														: catalogFilter !== "all"
															? "No connectors match this filter"
															: "No connectors found"}
												</p>
												<p className="mx-auto max-w-sm text-xs text-muted-foreground">
													Install a custom connector from URL, or create an
													agent that links the connector your workflow needs.
												</p>
											</div>
											{ownerSlug ? (
												<Button asChild variant="outline" size="sm">
													<Link
														to={`/${ownerSlug}/agents` as "/"}
														search={{ create: true }}
													>
														<Plus className="mr-2 h-4 w-4" />
														Create with an agent
													</Link>
												</Button>
											) : null}
										</div>
									);
								}
								return (
									<div className="space-y-4">
										{installedRows.length > 0 ? (
											<div className="space-y-1.5">
												<h3 className="text-[11.5px] font-medium text-muted-foreground">
													Already in this workspace
												</h3>
												<div className="overflow-hidden rounded-lg border">
													{installedRows.map(
														renderConnectorIn(installedRows.length),
													)}
												</div>
											</div>
										) : null}
										{catalogRows.length + synthRowCount > 0 ? (
											<div className="space-y-1.5">
												{installedRows.length > 0 ? (
													<h3 className="text-[11.5px] font-medium text-muted-foreground">
														From the catalog
													</h3>
												) : null}
												<div className="overflow-hidden rounded-lg border">
													{catalogRows.map(
														renderConnectorIn(
															catalogRows.length + synthRowCount,
														),
													)}
													{providerRows.map(renderProvider)}
													{sandboxKindRows.map(renderSandboxKind)}
													{appRows.map(renderApp)}
												</div>
											</div>
										) : null}
									</div>
								);
							})()
						)}
					</div>
				)}

			{/* Connection Configuration Form */}
			{form.selectedConnector && (
				<div className="space-y-4">
					{!form.isEditMode && (
						<ConnectionSetupChoices
							organizationId={organizationId}
							connectorKey={form.selectedConnector.key}
						/>
					)}
					<div className="flex items-center gap-2">
						{!form.isEditMode && (
							<Button
								variant="ghost"
								size="sm"
								onClick={form.handleBack}
								className="h-7 px-2 text-xs"
							>
								Choose another connector
							</Button>
						)}
						<ConnectorDisplay
							connector={form.selectedConnector}
							showDescription={false}
							className="gap-2"
						/>
						{form.selectedConnector.version && (
							<Badge
								variant="secondary"
								className="text-[10px] px-1.5 py-0 h-4 font-mono"
							>
								v{form.selectedConnector.version}
							</Badge>
						)}
					</div>

					{/* Pending auth banner */}
					{form.isEditMode &&
						editingConnection?.status === "pending_auth" &&
						editingConnection.connect_token && (
							<div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 space-y-3">
								<p className="text-sm font-medium text-amber-900 dark:text-amber-200">
									Authorization required
								</p>
								<p className="text-xs text-amber-700 dark:text-amber-300">
									This connection is waiting for OAuth authorization to
									complete.
								</p>
								<Button
									size="sm"
									onClick={() => {
										const startUrl = `${CONNECTOR_AUTH_BASE_URL}/connect/${editingConnection.connect_token}/oauth/start`;
										form.navigateOAuthPopup(startUrl);
									}}
								>
									<ExternalLink className="mr-1.5 h-3.5 w-3.5" />
									Authorize
								</Button>
							</div>
						)}

					{form.isEditMode &&
						editingConnection?.status === "pending_auth" &&
						editingConnection.auth_profile_kind === "browser_session" &&
						editingConnection.auth_profile_slug && (
							<div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-4 space-y-3">
								<p className="text-sm font-medium text-amber-900 dark:text-amber-200">
									Browser auth required
								</p>
								<p className="text-xs text-amber-700 dark:text-amber-300">
									{form.liveBrowser
										? "Sign in to this account in the selected Chrome profile, then save changes to verify it."
										: "Capture or refresh the browser session for this auth profile, then the connection will resume automatically."}
								</p>
								<p className="text-xs text-muted-foreground">
									Follow the connector’s authorization instructions to refresh
									this session.
								</p>
							</div>
						)}

					{/* Run on: serverless (in the Lobu server) vs a specific device worker */}
					{!isAppInstallCreateFlow &&
						(form.requiredCapability || form.deviceOptions.length > 0) && (
							<div className="space-y-2">
								<div className="flex items-center justify-between gap-2">
									{/* biome-ignore lint/a11y/noLabelWithoutControl: label for custom Select component */}
									<label className="text-sm font-medium">
										{form.browserRequired ? "Browser" : "Run on"}
										{form.requiredCapability ? (
											<span className="text-destructive ml-1">*</span>
										) : (
											<span className="text-xs font-normal text-muted-foreground ml-1">
												(optional)
											</span>
										)}
									</label>
									<div className="flex items-center gap-3">
										<button
											type="button"
											onClick={() => void form.refreshDevices()}
											disabled={form.devicesRefreshing}
											className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
										>
											<RefreshCw
												className={`h-3 w-3 ${form.devicesRefreshing ? "animate-spin" : ""}`}
											/>
											Refresh
										</button>
									</div>
								</div>
								<Select
									value={
										form.selectedDeviceWorkerId ??
										(form.requiredCapability ? "" : SERVERLESS_DEVICE_VALUE)
									}
									onValueChange={(v) => {
										if (v === ADD_DEVICE_VALUE) {
											if (ownerSlug) {
												// Pairing INSTRUCTIONS, not the device list — someone
												// with no device needs the install recipe, and landing
												// them on an empty list made them find it themselves.
												void navigate({
													to: "/$owner/connectors/create",
													params: { owner: ownerSlug },
												});
											}
											return;
										}
										form.setSelectedDeviceWorkerId(
											v === SERVERLESS_DEVICE_VALUE ? undefined : v,
										);
									}}
									disabled={
										!!form.lockedDeviceWorkerIdFromProfile ||
										!!form.pendingBrowserConnectionId
									}
								>
									<SelectTrigger className="h-8">
										<SelectValue
											placeholder={
												form.browserRequired
													? "Select a Chrome browser"
													: "Select a device"
											}
										/>
									</SelectTrigger>
									<SelectContent>
										{!form.requiredCapability && (
											<SelectItem value={SERVERLESS_DEVICE_VALUE}>
												Serverless (default)
											</SelectItem>
										)}
										{form.deviceOptions.map((device) => (
											<DeviceSelectItem key={device.id} device={device} />
										))}
										{ownerSlug ? (
											<SelectItem value={ADD_DEVICE_VALUE}>
												<span className="flex items-center gap-2 text-muted-foreground">
													<Plus className="h-3.5 w-3.5" />
													Add device
												</span>
											</SelectItem>
										) : null}
									</SelectContent>
								</Select>
								{form.requiredCapability && form.deviceOptions.length === 0 ? (
									<p className="text-xs text-muted-foreground">
										{form.browserRequired ? (
											`${form.browserSetupHint} Then `
										) : (
											<>
												No device has granted the &ldquo;
												{CAPABILITY_LABELS[form.requiredCapability] ??
													form.requiredCapability}
												&rdquo; permission. Open the Lobu app on a device and
												grant it, then{" "}
											</>
										)}
										<button
											type="button"
											onClick={() => void form.refreshDevices()}
											className="underline hover:text-foreground"
										>
											refresh
										</button>
										.
									</p>
								) : form.selectedDeviceWorkerId ? (
									<p className="text-xs text-muted-foreground">
										{form.browserRequired
											? "Lobu uses this Chrome profile and its signed-in account. Keep this browser open when using the connection."
											: "Syncs and actions run on this device — with its credentials and network, not the Lobu server."}
									</p>
								) : (
									<p className="text-xs text-muted-foreground">
										Runs serverless, in the Lobu server.
									</p>
								)}
							</div>
						)}

					{/* Edit mode: shared action policies and runner */}
					{form.isEditMode && editingConnection?.has_operations && (
						<div className="rounded-lg border bg-muted/30 p-4 space-y-4">
							<ConnectionActionsSection
								connection={editingConnection}
								ownerSlug={ownerSlug}
							/>
						</div>
					)}

					{/* Create mode: feeds & actions */}
					{!isAppInstallCreateFlow && !form.isEditMode && (
						<CreateModeCapabilities
							connector={form.selectedConnector}
							ownerSlug={ownerSlug}
						/>
					)}

					{/* Authentication */}
					{form.selectableMethods.length > 0 && (
						<section className="space-y-3">
							<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
								Authentication
							</p>

							{/* Method selector (only if >1 method) */}
							{form.selectableMethods.length > 1 ? (
								<div className="flex gap-2">
									{form.selectableMethods.map((method, index) => (
										<button
											key={`${method.type}-${getMethodLabel(method)}`}
											type="button"
											onClick={() => form.handleMethodChange(index)}
											disabled={!!form.pendingBrowserConnectionId}
											className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
												form.selectedAuthMethodIndex === index
													? "border-primary bg-primary/10 text-foreground"
													: "border-border bg-background text-muted-foreground hover:text-foreground"
											}`}
										>
											{getMethodLabel(method)}
										</button>
									))}
								</div>
							) : (
								<p className="text-xs text-muted-foreground">
									{getMethodLabel(form.selectableMethods[0])}
								</p>
							)}

							{form.activeMethod?.type !== "none" &&
								form.activeMethod?.description && (
									<p className="text-xs text-muted-foreground">
										{form.activeMethod.description}
									</p>
								)}

							{form.activeMethod?.type === "app_installation" && (
								<div className="space-y-3">
									{form.activeMethod.permissions &&
										form.activeMethod.permissions.length > 0 && (
											<div className="flex flex-wrap items-center gap-1.5">
												<span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
													Grants
												</span>
												{form.activeMethod.permissions.map((permission) => (
													<Badge
														key={permission}
														variant="secondary"
														className="h-4 px-1.5 py-0 text-[10px]"
													>
														{permission}
													</Badge>
												))}
											</div>
										)}
									<AppInstallButton
										organizationId={organizationId}
										connectorKey={form.selectedConnector.key}
										providerLabel={form.selectedConnector?.name}
										installShape={form.activeMethod.installShape}
										variant="default"
										size="default"
										className="w-full"
										label={`Install ${form.selectedConnector?.name ?? "App"}`}
									/>
									<p className="text-xs text-muted-foreground">
										You'll be sent to{" "}
										{form.activeMethod.provider.charAt(0).toUpperCase() +
											form.activeMethod.provider.slice(1)}{" "}
										to choose what to grant. The connection and its feeds are
										created automatically when you return.
									</p>
								</div>
							)}

							{form.activeMethod?.type === "env_keys" && (
								<div className="space-y-3">
									<div className="space-y-2">
										<p className="text-xs font-medium text-muted-foreground">
											Auth Profile
										</p>
										<div className="flex gap-1.5">
											<Select
												value={form.selectedAuthProfileSlug}
												onValueChange={form.setSelectedAuthProfileSlug}
											>
												<SelectTrigger className="h-8 flex-1 min-w-0">
													<SelectValue placeholder="Select auth profile" />
												</SelectTrigger>
												<SelectContent>
													{form.runtimeEnvProfiles.map((profile) => (
														<SelectItem key={profile.slug} value={profile.slug}>
															{profile.display_name} ({profile.slug})
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{form.selectedAuthProfileSlug &&
												(form.confirmingDeleteProfileSlug ===
												form.selectedAuthProfileSlug ? (
													<div className="flex items-center gap-1 shrink-0">
														<Button
															type="button"
															variant="destructive"
															size="sm"
															className="h-8 text-xs px-2"
															disabled={
																form.deleteAuthProfileMutation.isPending
															}
															onClick={() =>
																void form.handleDeleteAuthProfile(
																	form.selectedAuthProfileSlug!,
																	"env",
																)
															}
														>
															{form.deleteAuthProfileMutation.isPending ? (
																<Spinner size="sm" />
															) : (
																"Delete"
															)}
														</Button>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															className="h-8 text-xs px-2"
															onClick={() =>
																form.setConfirmingDeleteProfileSlug(null)
															}
														>
															Cancel
														</Button>
													</div>
												) : (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
														onClick={() =>
															form.setConfirmingDeleteProfileSlug(
																form.selectedAuthProfileSlug!,
															)
														}
													>
														<Trash2 className="h-3.5 w-3.5" />
													</Button>
												))}
										</div>
										{form.runtimeEnvProfiles.length === 0 && (
											<p className="text-xs text-muted-foreground">
												No auth profiles yet for this connector.
											</p>
										)}
									</div>

									<Button
										type="button"
										variant="outline"
										size="sm"
										className="h-8"
										onClick={() =>
											form.setShowNewRuntimeProfileForm((value) => !value)
										}
									>
										<Plus className="mr-1.5 h-3.5 w-3.5" />
										{form.showNewRuntimeProfileForm
											? "Hide New Profile"
											: "Create Auth Profile"}
									</Button>

									{form.showNewRuntimeProfileForm && (
										<div className="space-y-3 rounded-md border bg-background p-3">
											<Input
												placeholder="Profile name"
												value={form.newRuntimeProfileName}
												onChange={(event) =>
													form.setNewRuntimeProfileName(event.target.value)
												}
											/>
											<Input
												placeholder="profile-slug (optional)"
												value={form.newRuntimeProfileSlug}
												onChange={(event) =>
													form.setNewRuntimeProfileSlug(event.target.value)
												}
											/>
											{form.runtimeDraft.revision && (
												<>
													<FormDraftConflict
														visible={form.runtimeDraft.hasConflict}
														onReload={form.runtimeDraft.reset}
													/>
													<DynamicConnectorForm
														schema={form.runtimeDraft.revision}
														values={form.newRuntimeProfileValues}
														onValuesChange={form.setNewRuntimeProfileValues}
														fieldIdPrefix="new-runtime-profile-"
													/>
												</>
											)}
											<div className="flex justify-end gap-2">
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														form.setShowNewRuntimeProfileForm(false);
														form.setNewRuntimeProfileName("");
														form.setNewRuntimeProfileSlug("");
														form.setNewRuntimeProfileValues({});
													}}
												>
													Cancel
												</Button>
												<Button
													type="button"
													size="sm"
													disabled={
														!form.runtimeDraft.canSubmit ||
														form.createAuthProfile.isPending ||
														Object.keys(
															form.normalizeStringValues(
																form.newRuntimeProfileValues,
															),
														).length === 0
													}
													onClick={() => void form.handleCreateRuntimeProfile()}
												>
													{form.createAuthProfile.isPending ? (
														<Spinner size="md" className="mr-1.5" />
													) : null}
													Create
												</Button>
											</div>
										</div>
									)}
								</div>
							)}

							{form.activeMethod?.type === "browser" && !form.liveBrowser && (
								<div className="space-y-3">
									<div className="space-y-2">
										<p className="text-xs font-medium text-muted-foreground">
											Browser Auth Profile
										</p>
										<div className="flex gap-1.5">
											<Select
												value={form.selectedAuthProfileSlug}
												onValueChange={(value) => {
													if (value === CREATE_BROWSER_PROFILE_VALUE) {
														form.setShowNewBrowserProfileForm(true);
														return;
													}
													form.setSelectedAuthProfileSlug(value);
													form.setShowNewBrowserProfileForm(false);
												}}
											>
												<SelectTrigger className="h-8 flex-1 min-w-0">
													<SelectValue placeholder="Select browser auth profile" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={CREATE_BROWSER_PROFILE_VALUE}>
														<Plus className="mr-1.5 inline h-3.5 w-3.5" />
														Create new browser profile
													</SelectItem>
													{form.browserProfiles.map((profile) => {
														const deviceLabel = profile.device_worker_id
															? form.devices.find(
																	(d) => d.id === profile.device_worker_id,
																)?.label
															: null;
														return (
															<SelectItem
																key={profile.slug}
																value={profile.slug}
															>
																{profile.display_name} ({profile.slug})
																{deviceLabel ? ` — on ${deviceLabel}` : ""}
															</SelectItem>
														);
													})}
												</SelectContent>
											</Select>
											{form.selectedAuthProfileSlug &&
												(form.confirmingDeleteProfileSlug ===
												form.selectedAuthProfileSlug ? (
													<div className="flex items-center gap-1 shrink-0">
														<Button
															type="button"
															variant="destructive"
															size="sm"
															className="h-8 text-xs px-2"
															disabled={
																form.deleteAuthProfileMutation.isPending
															}
															onClick={() =>
																void form.handleDeleteAuthProfile(
																	form.selectedAuthProfileSlug!,
																	"browser",
																)
															}
														>
															{form.deleteAuthProfileMutation.isPending ? (
																<Spinner size="sm" />
															) : (
																"Delete"
															)}
														</Button>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															className="h-8 text-xs px-2"
															onClick={() =>
																form.setConfirmingDeleteProfileSlug(null)
															}
														>
															Cancel
														</Button>
													</div>
												) : (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
														onClick={() =>
															form.setConfirmingDeleteProfileSlug(
																form.selectedAuthProfileSlug!,
															)
														}
													>
														<Trash2 className="h-3.5 w-3.5" />
													</Button>
												))}
										</div>
										{form.browserProfiles.length === 0 && (
											<p className="text-xs text-muted-foreground">
												No browser auth profiles yet for this connector.
											</p>
										)}
									</div>

									{form.selectedAuthProfileSlug &&
										(() => {
											const profile = form.browserProfiles.find(
												(p) => p.slug === form.selectedAuthProfileSlug,
											);
											if (!profile) return null;
											return (
												<div className="space-y-2 rounded-md border bg-background p-3">
													<div className="flex flex-wrap gap-2">
														<Badge
															variant={
																profile.status === "active"
																	? "default"
																	: "secondary"
															}
														>
															{profile.status}
														</Badge>
														{typeof profile.cookie_count === "number" && (
															<Badge variant="outline">
																{profile.cookie_count} cookies
															</Badge>
														)}
														{profile.auth_cookie_name && (
															<Badge variant="outline">
																{profile.auth_cookie_name}
															</Badge>
														)}
													</div>
													<p className="text-xs text-muted-foreground">
														Follow the connector’s authorization instructions to
														refresh this session.
													</p>
												</div>
											);
										})()}

									<Button
										type="button"
										variant="outline"
										size="sm"
										className="h-8"
										onClick={() =>
											form.setShowNewBrowserProfileForm((value) => !value)
										}
									>
										<Plus className="mr-1.5 h-3.5 w-3.5" />
										{form.showNewBrowserProfileForm
											? "Hide New Profile"
											: "Create Browser Profile"}
									</Button>

									{form.showNewBrowserProfileForm && (
										<div className="space-y-3 rounded-md border bg-background p-3">
											<Input
												placeholder="Profile name"
												value={form.newBrowserProfileName}
												onChange={(event) =>
													form.setNewBrowserProfileName(event.target.value)
												}
											/>
											<Input
												placeholder="profile-slug (optional)"
												value={form.newBrowserProfileSlug}
												onChange={(event) =>
													form.setNewBrowserProfileSlug(event.target.value)
												}
											/>

											<p className="text-xs text-muted-foreground">
												Create the session profile, then follow the connector’s
												authorization instructions.
											</p>
											<div className="flex justify-end gap-2">
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														form.setShowNewBrowserProfileForm(false);
														form.setNewBrowserProfileName("");
														form.setNewBrowserProfileSlug("");
													}}
												>
													Cancel
												</Button>
												<Button
													type="button"
													size="sm"
													disabled={form.createAuthProfile.isPending}
													onClick={() => void form.handleCreateBrowserProfile()}
												>
													{form.createAuthProfile.isPending ? (
														<Spinner size="md" className="mr-1.5" />
													) : null}
													Create
												</Button>
											</div>
										</div>
									)}
								</div>
							)}

							{form.activeMethod?.type === "interactive" &&
								form.isEditMode &&
								editingConnection &&
								editingConnection.auth_profile_kind === "interactive" && (
									<InteractivePairingPanel
										connection={editingConnection}
										activeAuthRunId={form.pendingAuthRunId}
										onStart={() => {
											reauthenticate.mutate(
												{ connection_id: editingConnection.id },
												{
													onSuccess: (result) => {
														form.setPendingAuthRunId(
															result.auth_run_id ?? null,
														);
													},
												},
											);
										}}
										onClear={() => form.setPendingAuthRunId(null)}
										onRecover={(runId) => form.setPendingAuthRunId(runId)}
										isStarting={reauthenticate.isPending}
									/>
								)}

							{form.activeMethod?.type === "oauth" && (
								<div className="space-y-4">
									{/* Workspace app — compact in create mode when already set up */}
									{!form.isEditMode &&
									form.appOAuthProfiles.length > 0 &&
									form.selectedAppAuthProfileSlug ? (
										<div className="space-y-1">
											<div className="flex items-center justify-between gap-2">
												<p className="text-xs font-medium text-muted-foreground">
													Workspace app
												</p>
												{form.isAdmin &&
													ownerSlug &&
													form.selectedConnector && (
														<Link
															to="/$owner/connectors/$connectorKey"
															params={{
																owner: ownerSlug,
																connectorKey: form.selectedConnector.key,
															}}
															search={{ setup: "oauth_app" }}
															className="text-xs text-muted-foreground hover:text-foreground"
														>
															Manage app
														</Link>
													)}
											</div>
											{form.appOAuthProfiles.length === 1 || !form.isAdmin ? (
												// Non-admins are server-side restricted to the
												// org default; rendering the picker would be a
												// dead control and a wrong affordance. Show the
												// resolved profile name read-only.
												(() => {
													const resolved = form.appOAuthProfiles.find(
														(p) => p.slug === form.selectedAppAuthProfileSlug,
													);
													return (
														<div className="flex items-center gap-2">
															<p className="text-sm">
																{resolved?.display_name ??
																	"Ask an administrator to set the workspace-default app."}
															</p>
															{resolved?.is_default_for_connector &&
																resolved.connector_key ===
																	form.selectedConnector?.key && (
																	<span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
																		Org default
																	</span>
																)}
														</div>
													);
												})()
											) : (
												<Select
													value={form.selectedAppAuthProfileSlug}
													onValueChange={form.setSelectedAppAuthProfileSlug}
												>
													<SelectTrigger className="h-8">
														<SelectValue placeholder="Choose an app" />
													</SelectTrigger>
													<SelectContent>
														{form.appOAuthProfiles.map((profile) => (
															<SelectItem
																key={profile.slug}
																value={profile.slug}
															>
																{profile.display_name}
																{profile.is_default_for_connector &&
																profile.connector_key ===
																	form.selectedConnector?.key
																	? " (default)"
																	: ""}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											)}
										</div>
									) : (
										<div className="space-y-2">
											<div className="flex items-center justify-between gap-2">
												<p className="text-xs font-medium text-muted-foreground">
													Workspace app
												</p>
												{form.isAdmin &&
													ownerSlug &&
													form.selectedConnector &&
													form.appOAuthProfiles.length > 0 && (
														<Link
															to="/$owner/connectors/$connectorKey"
															params={{
																owner: ownerSlug,
																connectorKey: form.selectedConnector.key,
															}}
															className="text-xs text-muted-foreground hover:text-foreground"
														>
															Manage
														</Link>
													)}
											</div>
											<div className="flex gap-1.5">
												<Select
													value={form.selectedAppAuthProfileSlug}
													onValueChange={(value) => {
														if (value === CREATE_OAUTH_APP_PROFILE_VALUE) {
															form.setShowNewAppProfileForm(true);
															return;
														}
														form.setSelectedAppAuthProfileSlug(value);
														form.setShowNewAppProfileForm(false);
													}}
												>
													<SelectTrigger className="h-8 flex-1 min-w-0">
														<SelectValue
															placeholder={`Select ${(form.activeMethod as OAuthMethod).provider} app profile`}
														/>
													</SelectTrigger>
													<SelectContent>
														{/* Only admins can create app profiles — they're an
                                    org-shared credential, not a per-user setting. */}
														{form.isAdmin && (
															<SelectItem
																value={CREATE_OAUTH_APP_PROFILE_VALUE}
															>
																<Plus className="mr-1.5 inline h-3.5 w-3.5" />
																Create new app profile
															</SelectItem>
														)}
														{form.appOAuthProfiles.map((profile) => (
															<SelectItem
																key={profile.slug}
																value={profile.slug}
															>
																{profile.display_name} ({profile.slug})
																{profile.is_default_for_connector &&
																profile.connector_key ===
																	form.selectedConnector?.key
																	? " — default"
																	: ""}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												{form.selectedAppAuthProfileSlug &&
													(form.confirmingDeleteProfileSlug ===
													form.selectedAppAuthProfileSlug ? (
														<div className="flex items-center gap-1 shrink-0">
															<Button
																type="button"
																variant="destructive"
																size="sm"
																className="h-8 text-xs px-2"
																disabled={
																	form.deleteAuthProfileMutation.isPending
																}
																onClick={() =>
																	void form.handleDeleteAuthProfile(
																		form.selectedAppAuthProfileSlug!,
																		"app",
																	)
																}
															>
																{form.deleteAuthProfileMutation.isPending ? (
																	<Spinner size="sm" />
																) : (
																	"Delete"
																)}
															</Button>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																className="h-8 text-xs px-2"
																onClick={() =>
																	form.setConfirmingDeleteProfileSlug(null)
																}
															>
																Cancel
															</Button>
														</div>
													) : (
														<Button
															type="button"
															variant="ghost"
															size="sm"
															className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
															onClick={() =>
																form.setConfirmingDeleteProfileSlug(
																	form.selectedAppAuthProfileSlug!,
																)
															}
														>
															<Trash2 className="h-3.5 w-3.5" />
														</Button>
													))}
											</div>
											{form.appOAuthProfiles.length === 0 && (
												<p className="text-xs text-muted-foreground">
													{form.isAdmin
														? `To use your own app, configure its client credentials here. You can also use a managed app when available.`
														: `Choose a managed option if available, or ask an admin to configure your own ${(form.activeMethod as OAuthMethod).provider} app.`}
												</p>
											)}
											{form.showNewAppProfileForm && (
												<div className="space-y-3 rounded-md border bg-background p-3">
													<OAuthSetupInfo
														method={form.activeMethod as OAuthMethod}
													/>
													<Input
														placeholder="App profile name"
														value={form.newAppProfileName}
														onChange={(event) =>
															form.setNewAppProfileName(event.target.value)
														}
													/>
													<Input
														placeholder="app-profile-slug (optional)"
														value={form.newAppProfileSlug}
														onChange={(event) =>
															form.setNewAppProfileSlug(event.target.value)
														}
													/>
													{form.appDraft.revision && (
														<>
															<FormDraftConflict
																visible={form.appDraft.hasConflict}
																onReload={form.appDraft.reset}
															/>
															<DynamicConnectorForm
																schema={form.appDraft.revision}
																values={form.newAppProfileValues}
																onValuesChange={form.setNewAppProfileValues}
																fieldIdPrefix="new-app-profile-"
															/>
														</>
													)}
													<div className="flex justify-end gap-2">
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => {
																form.setShowNewAppProfileForm(false);
																form.setNewAppProfileName("");
																form.setNewAppProfileSlug("");
																form.setNewAppProfileValues({});
															}}
														>
															Cancel
														</Button>
														<Button
															type="button"
															size="sm"
															disabled={
																!form.appDraft.canSubmit ||
																form.createAuthProfile.isPending ||
																Object.keys(
																	form.normalizeStringValues(
																		form.newAppProfileValues,
																	),
																).length === 0
															}
															onClick={() => void form.handleCreateAppProfile()}
														>
															{form.createAuthProfile.isPending ? (
																<Spinner size="md" className="mr-1.5" />
															) : null}
															Create
														</Button>
													</div>
												</div>
											)}
										</div>
									)}

									<div className="space-y-2">
										<p className="text-xs font-medium text-muted-foreground">
											Your account
										</p>
										<div className="flex gap-1.5">
											<Select
												value={form.selectedAuthProfileSlug}
												onValueChange={(value) => {
													if (value === CREATE_OAUTH_ACCOUNT_PROFILE_VALUE) {
														form.setShowNewOAuthAccountForm(true);
														return;
													}
													form.setSelectedAuthProfileSlug(value);
													form.setShowNewOAuthAccountForm(false);
												}}
											>
												<SelectTrigger className="h-8 flex-1 min-w-0">
													<SelectValue
														placeholder={`Select ${(form.activeMethod as OAuthMethod).provider} account profile`}
													/>
												</SelectTrigger>
												<SelectContent>
													<SelectItem
														value={CREATE_OAUTH_ACCOUNT_PROFILE_VALUE}
													>
														<Plus className="mr-1.5 inline h-3.5 w-3.5" />
														Add new account
													</SelectItem>
													{form.runtimeOAuthProfiles.map((profile) => (
														<SelectItem key={profile.slug} value={profile.slug}>
															{profile.display_name} ({profile.slug}) [
															{profile.status}]
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{form.selectedAuthProfileSlug &&
												(form.confirmingDeleteProfileSlug ===
												form.selectedAuthProfileSlug ? (
													<div className="flex items-center gap-1 shrink-0">
														<Button
															type="button"
															variant="destructive"
															size="sm"
															className="h-8 text-xs px-2"
															disabled={
																form.deleteAuthProfileMutation.isPending
															}
															onClick={() =>
																void form.handleDeleteAuthProfile(
																	form.selectedAuthProfileSlug!,
																	"account",
																)
															}
														>
															{form.deleteAuthProfileMutation.isPending ? (
																<Spinner size="sm" />
															) : (
																"Delete"
															)}
														</Button>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															className="h-8 text-xs px-2"
															onClick={() =>
																form.setConfirmingDeleteProfileSlug(null)
															}
														>
															Cancel
														</Button>
													</div>
												) : (
													<Button
														type="button"
														variant="ghost"
														size="sm"
														className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-destructive"
														onClick={() =>
															form.setConfirmingDeleteProfileSlug(
																form.selectedAuthProfileSlug!,
															)
														}
													>
														<Trash2 className="h-3.5 w-3.5" />
													</Button>
												))}
										</div>
										{(() => {
											const selectedProfile = form.runtimeOAuthProfiles.find(
												(p) => p.slug === form.selectedAuthProfileSlug,
											);
											return (
												<div className="space-y-2">
													<OAuthScopeSelector
														requiredScopes={getRequiredOAuthScopes(
															form.activeMethod as OAuthMethod,
														)}
														optionalScopes={form.availableOAuthOptionalScopes}
														selectedOptionalScopes={
															form.selectedOAuthOptionalScopes
														}
														grantedScopes={selectedProfile?.granted_scopes}
														onToggleOptionalScope={(scope, checked) => {
															form.setSelectedOAuthOptionalScopes((current) =>
																checked
																	? [...new Set([...current, scope])]
																	: current.filter((value) => value !== scope),
															);
														}}
													/>
													{selectedProfile ? (
														<Button
															type="button"
															variant="outline"
															size="sm"
															className="h-8"
															disabled={form.reconnectAuthProfile.isPending}
															onClick={() =>
																void form.handleReconnect(selectedProfile.slug)
															}
														>
															{form.reconnectAuthProfile.isPending ? (
																<Spinner size="md" className="mr-1.5" />
															) : (
																<LogIn className="mr-1.5 h-3.5 w-3.5" />
															)}
															Review permissions
														</Button>
													) : null}
												</div>
											);
										})()}
										{form.runtimeOAuthProfiles.length === 0 && (
											<p className="text-xs text-muted-foreground">
												No OAuth account profiles yet for this connector.
											</p>
										)}
										{form.showNewOAuthAccountForm && (
											<div className="space-y-3 rounded-md border bg-background p-3">
												<Input
													placeholder="Account profile name"
													value={form.newOAuthAccountName}
													onChange={(event) =>
														form.setNewOAuthAccountName(event.target.value)
													}
												/>
												<Input
													placeholder="account-profile-slug (optional)"
													value={form.newOAuthAccountSlug}
													onChange={(event) =>
														form.setNewOAuthAccountSlug(event.target.value)
													}
												/>
												<p className="text-xs text-muted-foreground">
													Creating the profile will open the OAuth authorization
													page in a new tab. When it finishes, the profile
													becomes reusable for future connections.
												</p>
												<OAuthScopeSelector
													requiredScopes={getRequiredOAuthScopes(
														form.activeMethod as OAuthMethod,
													)}
													optionalScopes={form.availableOAuthOptionalScopes}
													selectedOptionalScopes={
														form.selectedOAuthOptionalScopes
													}
													onToggleOptionalScope={(scope, checked) => {
														form.setSelectedOAuthOptionalScopes((current) =>
															checked
																? [...new Set([...current, scope])]
																: current.filter((value) => value !== scope),
														);
													}}
												/>
												<div className="flex justify-end gap-2">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => {
															form.setShowNewOAuthAccountForm(false);
															form.setNewOAuthAccountName("");
															form.setNewOAuthAccountSlug("");
														}}
													>
														Cancel
													</Button>
													<Button
														type="button"
														size="sm"
														disabled={form.createAuthProfile.isPending}
														onClick={() =>
															void form.handleCreateOAuthAccountProfile()
														}
													>
														{form.createAuthProfile.isPending ? (
															<Spinner size="md" className="mr-1.5" />
														) : null}
														Continue
													</Button>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</section>
					)}

					{/* Optional metadata sits BELOW authentication. All three of these
					    are gated on !isAppInstallCreateFlow, so above the selector they
					    vanished the moment you picked an app-install method and the
					    selector jumped up the page — the control moved as a result of
					    using it. Below it, choosing a method never shifts the chooser. */}
					{!isAppInstallCreateFlow && (
						<div className="space-y-2">
							<label
								className="text-sm font-medium"
								htmlFor="conn-display-name"
							>
								Display Name
							</label>
							<Input
								id="conn-display-name"
								placeholder={`e.g. Work ${form.selectedConnector.name}`}
								value={form.displayName}
								onChange={(e) => form.setDisplayName(e.target.value)}
							/>
						</div>
					)}

					{/* Assign to member (admin only, create mode) */}
					{!isAppInstallCreateFlow &&
						!form.isEditMode &&
						form.isAdmin &&
						!form.liveBrowser && (
							<div className="space-y-2">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: label for custom MemberPicker component */}
								<label className="text-sm font-medium">
									Assign to Member{" "}
									<span className="text-xs font-normal text-muted-foreground">
										(optional)
									</span>
								</label>
								<MemberPicker
									organizationId={organizationId}
									value={form.selectedMemberUserId}
									onValueChange={form.setSelectedMemberUserId}
								/>
								<p className="text-xs text-muted-foreground">
									{form.selectedMemberUserId
										? "Only the selected member will use this connection."
										: "Leave empty to make this connection available to everyone in the organization."}
								</p>
								{form.existingNoAuthConnection && (
									<div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
										<p className="text-xs text-amber-700 dark:text-amber-300">
											This user already has a {form.selectedConnector.name}{" "}
											connection. No-auth connectors are limited to one per
											user.
										</p>
									</div>
								)}
							</div>
						)}

					{/* Editable config form from options_schema */}
					{form.hasUniqueOptionsFields && (
						<div className="rounded-lg border bg-muted/30 p-4 space-y-3">
							<p className="text-sm font-medium">Connection Options</p>
							<FormDraftConflict
								visible={form.configDraft.hasConflict}
								onReload={form.configDraft.reset}
							/>
							<DynamicConnectorForm
								schema={form.configDraft.revision ?? undefined}
								values={form.configValues}
								onValuesChange={form.handleValuesChange}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);

	const footerInner =
		form.selectedConnector && !form.previewConnector ? (
			<>
				{form.authorizationUrl && (
					<a
						className="text-sm underline"
						href={form.authorizationUrl}
						target="_blank"
						rel="noreferrer"
					>
						Continue authorization in your browser
					</a>
				)}
				{form.saveError && (
					<div className="w-full mb-3 rounded-md bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-600 dark:text-red-400">
						{form.saveError}
					</div>
				)}
				<div className="flex items-center w-full">
					{form.isEditMode && (
						<div className="mr-auto">
							{!form.showDeleteConfirm ? (
								<Button
									variant="ghost"
									size="sm"
									className="text-destructive hover:text-destructive"
									onClick={() => form.setShowDeleteConfirm(true)}
								>
									<Trash2 className="mr-1.5 h-3.5 w-3.5" />
									Delete
								</Button>
							) : (
								<div className="flex items-center gap-2">
									<Button
										variant="destructive"
										size="sm"
										disabled={form.deleteConnection.isPending}
										onClick={() => {
											form.deleteConnection.mutate(editingConnection!.id, {
												onSuccess: () => form.handleOpenChange(false),
											});
										}}
									>
										{form.deleteConnection.isPending ? (
											<>
												<Spinner size="md" className="mr-1.5" />
												Deleting...
											</>
										) : (
											"Confirm"
										)}
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={() => form.setShowDeleteConfirm(false)}
										disabled={form.deleteConnection.isPending}
									>
										Cancel
									</Button>
								</div>
							)}
						</div>
					)}
					{/* App-installation connectors install via a redirect (the
					 *  "Install" button in the auth panel), not a form submit — so
					 *  there's no "Add connection" action to render in create mode. */}
					{!(
						!form.isEditMode && form.activeMethod?.type === "app_installation"
					) && (
						<Button
							onClick={form.handleSave}
							disabled={
								!form.configDraft.canSubmit ||
								form.isSaving ||
								!!form.existingNoAuthConnection
							}
						>
							{form.isSaving ? (
								<>
									<Spinner size="lg" className="mr-2" />
									{form.isEditMode ? "Saving..." : "Creating..."}
								</>
							) : form.isEditMode ? (
								<>Save Changes</>
							) : (
								<>
									<Plus className="mr-2 h-4 w-4" />
									{form.pendingBrowserConnectionId
										? "Verify connection"
										: "Add connection"}
								</>
							)}
						</Button>
					)}
				</div>
			</>
		) : null;

	const footer = footerInner ? <div className="mt-6">{footerInner}</div> : null;

	const authDialog = (
		<AuthFlowDialog
			runId={
				form.isEditMode && form.activeMethod?.type === "interactive"
					? null
					: form.pendingAuthRunId
			}
			onClose={form.handleAuthClose}
		/>
	);

	return (
		<>
			{header}
			{body}
			{footer}
			{authDialog}
		</>
	);
}

/**
 * Credential form for a picked sandbox provider kind, rendered inside the
 * "Add a connection" page.
 *
 * A sandbox is not a `connection` row, so it cannot go through
 * `form.selectedConnector` — but from the user's side it is the same act
 * (pick a thing from the catalog, give it a credential), so it gets the same
 * Back + chip header and the same page chrome rather than its own surface.
 */
function SandboxKindCreatePanel({
	providerKind,
	credentialFields,
	isProviderCatalogLoading,
	ownerSlug,
	onBack,
}: {
	providerKind: string;
	credentialFields?: SandboxProviderCatalogItem["credentialFields"];
	isProviderCatalogLoading: boolean;
	ownerSlug: string;
	onBack: () => void;
}) {
	const navigate = useNavigate();
	const createSandbox = useCreateSandbox();
	const label = sandboxProviderLabel(providerKind);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					size="sm"
					onClick={onBack}
					className="h-7 px-2 text-xs"
				>
					Choose another provider
				</Button>
				<ConnectorDisplay
					connector={{
						key: `${SANDBOX_PROVIDER_KEY_PREFIX}${providerKind}`,
						name: label,
						favicon_domain: sandboxProviderFaviconDomain(providerKind),
					}}
					showDescription={false}
					className="gap-2"
				/>
				<FacetChips facets={sandboxCatalogFacets()} />
			</div>
			{isProviderCatalogLoading ? (
				<FormLoadingShell rows={4} className="max-w-md" />
			) : credentialFields ? (
				<CreateSandboxForm
					providerKind={providerKind}
					credentialFields={credentialFields}
					pending={createSandbox.isPending}
					onCancel={onBack}
					onCreate={(payload) =>
						createSandbox.mutate(payload, {
							onSuccess: (result) => {
								const id = result?.sandbox?.id;
								void navigate({
									to: id
										? "/$owner/connectors/$connectorKey"
										: "/$owner/connectors",
									params: id
										? { owner: ownerSlug, connectorKey: `sandbox:${id}` }
										: { owner: ownerSlug },
								});
							},
						})
					}
				/>
			) : (
				<p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
					This runtime provider is not available from the gateway.
				</p>
			)}
		</div>
	);
}
